<template>
    <div class="border border-slate-200 bg-white shadow-sm" :class="overview ? 'rounded p-3' : 'rounded-xl p-4'">
        <p v-if="iterationCreated" class="mb-3 text-sm text-slate-600">Updated campaign launched. Review the new contacts, then turn on autopilot when you're ready.</p>
        <p class="mb-2 text-xs text-slate-500" aria-live="polite">{{ stateCopy }}</p>
        <QualityScorePanel :evaluation="evaluation" :loading="isLoadingState" :show-actions="false" />
        <UButton v-if="!overview" class="mt-3" color="neutral" variant="outline" @click="emit('improve')">Review narration with AI</UButton>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import QualityScorePanel from '../campaign-detail/QualityScorePanel.vue';
import type { CampaignEvaluationOverview } from '~/composables/client';

const props = defineProps<{
    campaignId: number;
    iterationCreated?: boolean;
    launchWorkSessionId?: number | null;
    overview?: boolean;
}>();

const overview = computed(() => props.overview === true);

const emit = defineEmits<{
    improve: [];
}>();

const evaluation = ref<CampaignEvaluationOverview>(null);
const loading = ref(true);
const timedOut = ref(false);
let pollTimer: ReturnType<typeof setInterval> | null = null;
let attempts = 0;

onMounted(async () => {
    await $fetch(`/api/icps/${props.campaignId}/campaign-evaluation/ensure`, {
        method: 'POST',
        query: props.launchWorkSessionId ? { workSessionId: props.launchWorkSessionId } : undefined,
    }).catch(() => undefined);
    await pollEvaluation();
    pollTimer = setInterval(pollEvaluation, 5000);
});

onBeforeUnmount(() => {
    if (pollTimer) clearInterval(pollTimer);
});

async function pollEvaluation() {
    attempts++;
    loading.value = true;
    try {
        evaluation.value = await $fetch<CampaignEvaluationOverview>(`/api/icps/${props.campaignId}/campaign-evaluation`, {
            query: props.launchWorkSessionId ? { workSessionId: props.launchWorkSessionId } : undefined,
        });
        if (evaluation.value?.status === 'completed' || evaluation.value?.status === 'failed') {
            if (pollTimer) clearInterval(pollTimer);
            pollTimer = null;
        }
    } catch {
        // Keep the waiting state. The campaign may have navigated before API is ready.
    } finally {
        loading.value = false;
    }

    if (attempts >= 120 && pollTimer) {
        timedOut.value = true;
        clearInterval(pollTimer);
        pollTimer = null;
    }
}

const isLoadingState = computed(() => loading.value || !evaluation.value || evaluation.value.status === 'pending');

const stateCopy = computed(() => {
    if (evaluation.value?.status === 'completed' && evaluation.value.scores) return 'Stored evaluation loaded. Inspect sample provenance below.';
    if (evaluation.value?.status === 'failed') return 'Narration evaluation failed. You can still review and iterate manually.';
    if (evaluation.value?.status === 'pending') return 'Scoring this launch’s first review contacts.';
    if (timedOut.value) return 'Narration evaluation will appear once this launch’s review contacts are ready.';
    return 'Waiting for this launch’s Narration evaluation.';
});

</script>
